import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IncidentPriorityValue } from '@data';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Textarea } from '@ui/components/ui/textarea';
import { TimeField } from '@ui/components/ui/time-field';
import { WorkdayDateField } from '@app/components/shared/reportModal/WorkdayDateField';
import {
  PRIORITIES,
  priorityLabel,
} from '@app/components/pages/organizations/organization/clientReporting/product/sources/incidents';

import { NewIncidentDraft, newIncidentError } from './newIncident';

/** Logging the incident itself: one set of fields for all three doors, so each writes the same record. */
const NewIncidentFields = ({
  idPrefix,
  value,
  onChange,
  withDescription = false,
}: {
  /** Several of these can be on one screen — one per product on the day's report — so the ids differ. */
  idPrefix: string;
  value: NewIncidentDraft;
  onChange: (next: NewIncidentDraft) => void;
  /** The write-up: optional everywhere, and the one field that differs between the three doors. */
  withDescription?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const [reportedCalendarOpen, setReportedCalendarOpen] = useState(false);
  const [answeredCalendarOpen, setAnsweredCalendarOpen] = useState(false);

  const change = (next: Partial<NewIncidentDraft>): void => onChange({ ...value, ...next });
  const priorityOptions = PRIORITIES.map(entry => ({
    value: entry.toLowerCase(),
    label: priorityLabel(entry, t(`incidents.priorityName.${entry}`)),
  }));
  const answeredError = newIncidentError(value);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground">{t('reportIncident.newHint')}</p>

      {/* The name and how bad it was on one line, then the two moments: what it is, then when it
          happened. The name takes the room that is left. */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-[12rem] flex-1 flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-title`} required>
            {t('incidents.issueColumn')}
          </Label>
          <Input
            id={`${idPrefix}-title`}
            value={value.title}
            placeholder={t('incidents.titlePlaceholder')}
            onChange={event => change({ title: event.target.value })}
          />
        </div>
        <MultiSelectField
          single
          required
          clearable={false}
          searchable={false}
          // The panel is the trigger's width, so this is what the longest priority needs to read in full.
          className="w-64 shrink-0"
          id={`${idPrefix}-priority`}
          label={t('incidents.priority')}
          options={priorityOptions}
          value={value.priority ? [value.priority] : []}
          onChange={next => change({ priority: (next[0] ?? '') as IncidentPriorityValue })}
          emptyLabel={t('reportIncident.pickPriority')}
        />
      </div>

      {withDescription && (
        /* Two lines rather than the three it gets on the incident's own page: this is the account
           somebody can give while they are still mid-report, and the page it belongs to is where it gets
           written up properly. Unstarred — an incident is real without it. */
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-description`}>{t('incidents.description')}</Label>
          <Textarea
            id={`${idPrefix}-description`}
            rows={2}
            value={value.description}
            placeholder={t('incidents.descriptionPlaceholder')}
            onChange={event => change({ description: event.target.value })}
          />
        </div>
      )}

      {/* When it came in, and when we got back to them, a line each. Both carry a day of their own: the
          incident's is not the workday being reported — one reported on Friday can be worked on Monday —
          and the answer's is not the incident's either, since a callout that lands on Friday night is
          answered on Saturday. The answer's day follows the incident's until somebody picks another, so
          the ordinary case is two fields and not four.

          Unstarred, because an incident logged the minute it lands has not been answered yet. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-start gap-3">
          <WorkdayDateField
            required
            // Opens rightwards: inside a scrolling box, a right-hung panel opens into the left wall.
            align="start"
            className="w-48 shrink-0"
            id={`${idPrefix}-reported`}
            value={value.reportedDay}
            onChange={next => change({ reportedDay: next })}
            open={reportedCalendarOpen}
            onOpenChange={setReportedCalendarOpen}
            label={t('incidents.reportedDay')}
            placeholder={t('phillReport.pickDay')}
            disabledDays={{ after: new Date() }}
          />
          <TimeField
            required
            className="shrink-0"
            id={`${idPrefix}-reported-time`}
            label={t('reportIncident.atTime')}
            value={value.reportedTime}
            onChange={next => change({ reportedTime: next })}
            invalidMessage={t('reportIncident.badTime')}
          />
        </div>

        <div className="flex flex-wrap items-start gap-3">
          <WorkdayDateField
            align="start"
            className="w-48 shrink-0"
            id={`${idPrefix}-answered-day`}
            value={value.answeredDay ?? value.reportedDay}
            onChange={next => change({ answeredDay: next })}
            open={answeredCalendarOpen}
            onOpenChange={setAnsweredCalendarOpen}
            label={t('incidents.respondedDay')}
            placeholder={t('phillReport.pickDay')}
            // Nobody answered it before it came in, and nobody has answered it tomorrow.
            disabledDays={{ before: value.reportedDay, after: new Date() }}
          />
          <TimeField
            className="shrink-0"
            id={`${idPrefix}-answered`}
            label={t('reportIncident.atTime')}
            value={value.answeredTime}
            onChange={next => change({ answeredTime: next })}
            invalidMessage={t('reportIncident.badTime')}
          />
        </div>
        {answeredError && <p className="text-sm text-destructive">{t(answeredError)}</p>}
      </div>
    </div>
  );
};

export default NewIncidentFields;
